import { useMachine } from '@xstate/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReceivingSession } from '../data/receiving';
import { receivingSessionUpdatedPayloadSchema } from '../data/receiving-ws';
import { receivingLog } from '../lib/receiving-log';
import type { ReceivingService } from '../services/receiving';
import { receivingMachine } from '../state/receiving-machine';
import { useConnectionStatus } from '../websocket/use-connection-status';
import { useStoreWebSocket } from '../websocket/use-store-websocket';
import { useTabId } from './use-tab-id';
import { useTabRegistration } from './use-tab-registration';

export type UseReceivingSessionOptions = {
  storeId: string;
  sessionId?: string;
  service: ReceivingService;
};

export const useReceivingSession = ({
  storeId,
  sessionId,
  service,
}: UseReceivingSessionOptions) => {
  const [loaded, setLoaded] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  const input = useMemo(() => ({ storeId }), [storeId]);
  const [snapshot, send] = useMachine(receivingMachine, { input });

  const sendRef = useRef(send);
  sendRef.current = send;

  // Feed the machine a session the caller already has in hand — a fetch or
  // mutation response rather than a WebSocket push. The initial load uses it,
  // and so does POST /process, which answers with the finished `review`
  // session: applying that is what stops a dropped push (API Gateway 413s a
  // frame over 128 KB) from stranding the tab on "Processing invoices…". Both
  // paths carry the same session, so whichever lands first wins and the other
  // is a re-emission the machine allows.
  const applySession = useCallback((session: ReceivingSession) => {
    sendRef.current({ type: 'SESSION_UPDATED', session });
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setLoaded(true);

      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const session = await service.getSession(storeId, sessionId);
        if (cancelled) {
          return;
        }

        applySession(session);

        const claim = await service.claimSession(storeId, sessionId);
        if (cancelled) {
          return;
        }

        setReadOnly(!claim.granted);
      } catch {
        if (cancelled) {
          return;
        }
        // Session not found or error — leave readOnly false, context.session stays undefined
      } finally {
        if (!cancelled) {
          setLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storeId, sessionId, service, applySession]);

  // Announce this tab's viewer lease over WS. Fires on every open (including
  // reconnects) and whenever sessionId changes while open.
  useTabRegistration(sessionId);

  const { subscribe } = useStoreWebSocket();
  const connectionStatus = useConnectionStatus();

  // Re-acquire the viewer lease after a WebSocket reconnect. The backend
  // releases active_viewer_tab_id on $disconnect, which API Gateway fires
  // whenever this tab is backgrounded past its 10-minute idle timeout — the
  // normal case while the user is off in the AI assistant. partysocket
  // reconnects on its own and register_tab is re-sent, but neither restores
  // the lease, so without this the next /process call 409s until a manual
  // page refresh. The first 'open' is the initial connect, already claimed
  // by the effect above; only re-claim on a subsequent reconnect.
  const everOpenRef = useRef(false);
  useEffect(() => {
    if (connectionStatus !== 'open') {
      return;
    }

    if (!everOpenRef.current) {
      everOpenRef.current = true;

      return;
    }

    if (!sessionId) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const claim = await service.claimSession(storeId, sessionId);
        if (!cancelled) {
          setReadOnly(!claim.granted);
        }
      } catch {
        // Transient — a later reconnect or a page refresh will retry.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [connectionStatus, sessionId, storeId, service]);

  const tabId = useTabId();
  const prevStateRef = useRef<string | null>(null);
  useEffect(() => {
    const to = snapshot.value as string;
    const from = prevStateRef.current;
    if (from === to) {
      return;
    }

    prevStateRef.current = to;
    receivingLog('state_change', {
      session_id: sessionId ?? null,
      store_id: storeId,
      tab_id: tabId,
      from_state: from ?? 'mount',
      to_state: to,
    });
  }, [snapshot.value, sessionId, storeId, tabId]);

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    return subscribe(
      'receiving_session_updated',
      receivingSessionUpdatedPayloadSchema,
      (payload) => {
        if (payload.session.session_id !== sessionId) {
          return;
        }

        sendRef.current({
          type: 'SESSION_UPDATED',
          session: payload.session,
          probeRefused: payload.probe_refused,
          abandonBusy: payload.abandon_busy,
        });
      }
    );
  }, [subscribe, sessionId]);

  return {
    loaded,
    readOnly,
    state: snapshot.value as string,
    context: snapshot.context,
    send,
    applySession,
    connectionStatus,
  };
};
